/**
 * useEditorStore — Zustand store for the Newsletter Editor
 *
 * RULE: When collabActive === true, all section mutations must go through
 *       CollabDelegate (which syncs via Yjs before updating local state).
 */
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

// Type-specific data + setting overrides for new section types
const TYPE_DEFAULTS = {
  header: {
    data: {
      logoUrl: null, logoFetched: false,
      title: 'Newsletter Title', subtitle: '',
      bannerImageUrl: null,
    },
    settings: {
      padding: '14px 24px', backgroundColor: '#f4efe5',
      textColor: '#000000',
      titleSize: 28, subtitleSize: 14,
      fontFamily: "'Instrument Serif', serif", bannerHeight: 120,
    },
  },
  founders_focus: {
    data: { name: 'Bertin Dcruz', designation: 'Founder and CEO, Electronikmedia', quote: '' },
    settings: { padding: '28px 32px', backgroundColor: '#e9e0cc', textColor: '#000000', textAlign: 'left' },
  },
  meet_engineer: {
    data: { employeeId: null, name: '', photoUrl: null, jobTitle: '', qna: [] },
    settings: { padding: '28px 24px', backgroundColor: '#f4efe5', textColor: '#000000' },
  },
  appreciation: {
    data: { title: 'Appreciation', members: [] },
    settings: { padding: '28px 24px', backgroundColor: '#f4efe5', textColor: '#111827', accentColor: '#f54b01', columnsPerRow: 2 },
  },
  project_update: {
    data: { status: 'In Progress', html: '' },
    settings: { padding: '24px', backgroundColor: '#f4efe5', textColor: '#000000', accentColor: '#f54b01', columnsCount: 1 },
  },
  footer: {
    data: { contentHtml: '', socialLinks: [] },
    settings: { padding: '32px', backgroundColor: '#f4efe5', textColor: '#000000' },
  },
  image: {
    data: {
      heading: '',
      imageUrl: null,
      captionHtml: '',
      layout: ['image', 'caption'],
      captionAlignment: 'left',
    },
    settings: { padding: '24px', backgroundColor: '#e9e0cc', textColor: '#000000' },
  },
};

const DEFAULT_SECTION = (type = 'text') => {
  const extra  = TYPE_DEFAULTS[type] || {};
  const base   = {
    id:   uuidv4(),
    type, // 'text'|'image'|'divider'|'columns'|'hero'|'cta'|'spacer'|
          // 'header'|'founders_focus'|'meet_engineer'|'appreciation'|'project_update'|'footer'
    content: '',
    html:    '',
    settings: {
      padding:         '16px',
      backgroundColor: 'var(--em-bg-card)',
      textColor:       '#111827',
      fontSize:        '16px',
      fontFamily:      'Inter, sans-serif',
      textAlign:       'left',
      borderRadius:    '0px',
      accentColor:     '#f54b01',
      ...(extra.settings || {}),
    },
    mobileOverrides: { fontSize: null, padding: null, hidden: false },
  };
  if (extra.data) base.data = extra.data;
  return base;
};

const DEFAULT_META = {
  subject: '',
  previewText: '',
  fontFamily: 'Inter, sans-serif',
  backgroundColor: '#f4efe5',
  contentWidth: 680,
  headerColor: '#111827',
  accentColor: '#f54b01',
};

export const useEditorStore = create((set, get) => ({
  // ── State ────────────────────────────────────────────────────────────────
  newsletterId: null,
  title: 'Untitled Newsletter',
  sections: [],
  meta: DEFAULT_META,
  status: 'draft',

  selectedSectionId: null,
  activePanel: 'preview',   // 'sections' | 'preview' | 'settings'
  isMobile: false,
  dirty: false,
  saving: false,
  saveError: null,
  lastSavedAt: null,

  collabActive: false,
  collabUsers: [],
  collabDelegate: null,      // set by useCollaboration hook

  versions: [],
  versionPanelOpen: false,

  // ── Init ─────────────────────────────────────────────────────────────────
  init: (newsletter) => {
    set({
      newsletterId: newsletter.id,
      title: newsletter.title,
      sections: newsletter.sections || [],
      meta: { ...DEFAULT_META, ...(newsletter.meta || {}) },
      status: newsletter.status,
      dirty: false,
      selectedSectionId: null,
    });
  },

  // ── Title ─────────────────────────────────────────────────────────────────
  setTitle: (title) => set({ title, dirty: true }),

  // ── Meta ──────────────────────────────────────────────────────────────────
  updateMeta: (patch) =>
    set((s) => ({ meta: { ...s.meta, ...patch }, dirty: true })),

  // ── Sections (all mutations check collabActive) ───────────────────────────
  _applyOrDelegate: (mutation) => {
    const { collabActive, collabDelegate } = get();
    if (collabActive && collabDelegate) {
      collabDelegate(mutation);   // let Yjs handle it; state updated via Yjs observer
    } else {
      mutation(set, get);
    }
  },

  addSection: (type = 'text', afterId = null) => {
    get()._applyOrDelegate((set, get) => {
      const newSection = DEFAULT_SECTION(type);
      set((s) => {
        const sections = [...s.sections];
        if (afterId) {
          const idx = sections.findIndex((x) => x.id === afterId);
          sections.splice(idx + 1, 0, newSection);
        } else {
          sections.push(newSection);
        }
        return { sections, selectedSectionId: newSection.id, dirty: true };
      });
    });
  },

  updateSection: (id, patch) => {
    get()._applyOrDelegate((set) => {
      set((s) => ({
        sections: s.sections.map((sec) =>
          sec.id === id ? { ...sec, ...patch, dirty: undefined } : sec
        ),
        dirty: true,
      }));
    });
  },

  updateSectionSettings: (id, settingsPatch) => {
    get()._applyOrDelegate((set) => {
      set((s) => ({
        sections: s.sections.map((sec) =>
          sec.id === id
            ? { ...sec, settings: { ...sec.settings, ...settingsPatch } }
            : sec
        ),
        dirty: true,
      }));
    });
  },

  updateSectionMobileOverride: (id, patch) => {
    get()._applyOrDelegate((set) => {
      set((s) => ({
        sections: s.sections.map((sec) =>
          sec.id === id
            ? { ...sec, mobileOverrides: { ...sec.mobileOverrides, ...patch } }
            : sec
        ),
        dirty: true,
      }));
    });
  },

  removeSection: (id) => {
    get()._applyOrDelegate((set, get) => {
      set((s) => {
        const sections = s.sections.filter((x) => x.id !== id);
        const selectedSectionId =
          s.selectedSectionId === id ? (sections[0]?.id || null) : s.selectedSectionId;
        return { sections, selectedSectionId, dirty: true };
      });
    });
  },

  duplicateSection: (id) => {
    get()._applyOrDelegate((set) => {
      set((s) => {
        const idx = s.sections.findIndex((x) => x.id === id);
        if (idx === -1) return {};
        const clone = { ...s.sections[idx], id: uuidv4() };
        const sections = [...s.sections];
        sections.splice(idx + 1, 0, clone);
        return { sections, selectedSectionId: clone.id, dirty: true };
      });
    });
  },

  reorderSections: (newOrder) => {
    get()._applyOrDelegate((set) => {
      set({ sections: newOrder, dirty: true });
    });
  },

  // ── Selection ─────────────────────────────────────────────────────────────
  selectSection: (id) => set({ selectedSectionId: id }),

  // ── UI panels ─────────────────────────────────────────────────────────────
  setActivePanel: (panel) => set({ activePanel: panel }),
  setIsMobile: (v) => set({ isMobile: v }),

  // ── Save state helpers ────────────────────────────────────────────────────
  setSaving: (v) => set({ saving: v }),
  setSaveError: (err) => set({ saveError: err }),
  markSaved: () => set({ dirty: false, saving: false, lastSavedAt: new Date().toISOString() }),

  // ── Collab ────────────────────────────────────────────────────────────────
  setCollabActive: (v) => set({ collabActive: v }),
  setCollabUsers: (users) => set({ collabUsers: users }),
  setCollabDelegate: (fn) => set({ collabDelegate: fn }),

  // ── Versions panel ───────────────────────────────────────────────────────
  setVersions: (versions) => set({ versions }),
  toggleVersionPanel: () => set((s) => ({ versionPanelOpen: !s.versionPanelOpen })),

  // ── Hydrate sections from Yjs (called by useCollaboration) ───────────────
  hydrateSections: (sections) => set({ sections }),
}));
